import { useState } from 'react';
import { defaultProgram, defaultTiers } from '@/mocks/settings';
import { validateProgramSettings } from '@/lib/settings/validation';
import type { RedemptionPolicy } from '@/types/settings';

const inputClasses =
  'w-full bg-background-50 border border-background-200/90 rounded-xl px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-500 shadow-2xs transition-all';

export default function SettingsPage() {
  const [programName, setProgramName] = useState(defaultProgram.programName);
  const [pointsPerCurrency, setPointsPerCurrency] = useState(String(defaultProgram.pointsPerCurrency));
  const [signupBonus, setSignupBonus] = useState(String(defaultProgram.signupBonus));
  const [defaultTier, setDefaultTier] = useState(defaultProgram.defaultTier);
  const [redemptionPolicy, setRedemptionPolicy] = useState<RedemptionPolicy>(defaultProgram.redemptionPolicy);
  const [tiers, setTiers] = useState(defaultTiers);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleTierChange = (id: string, field: 'minPoints' | 'benefits', value: string) => {
    setTiers((prev) =>
      prev.map((tier) =>
        tier.id === id
          ? { ...tier, [field]: field === 'minPoints' ? (value === '' ? 0 : Number(value)) : value }
          : tier,
      ),
    );
  };

  const handleSave = () => {
    const points = Number(pointsPerCurrency);
    const bonus = Number(signupBonus);

    const validationError = validateProgramSettings({
      programName, pointsPerCurrency: points, signupBonus: bonus, defaultTier, redemptionPolicy,
    }, tiers);
    if (validationError) {
      setError(validationError);
      setSaved(false);
      return;
    }

    setError('');
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    setProgramName(defaultProgram.programName);
    setPointsPerCurrency(String(defaultProgram.pointsPerCurrency));
    setSignupBonus(String(defaultProgram.signupBonus));
    setDefaultTier(defaultProgram.defaultTier);
    setRedemptionPolicy(defaultProgram.redemptionPolicy);
    setTiers(defaultTiers);
    setError('');
    setSaved(false);
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-extrabold text-2xl text-foreground-950 tracking-tight">Program settings</h3>
          <p className="text-sm text-foreground-500 mt-0.5">Configure earning rules, tier thresholds, and your redemption policy.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleReset}
            className="whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-background-50 border border-background-200/90 text-sm font-semibold text-foreground-700 shadow-xs hover:bg-background-100 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-refresh-line text-primary-500"></i> Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-bold shadow-md shadow-primary-500/25 hover:from-primary-600 hover:to-primary-700 hover:shadow-lg hover:shadow-primary-500/35 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-check-line text-base"></i> Save changes
          </button>
        </div>
      </div>

      {saved && (
        <div className="flex items-center gap-2.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 text-sm text-emerald-800 shadow-xs animate-fade-in">
          <i className="ri-checkbox-circle-fill text-emerald-600 text-lg"></i>
          <span className="font-medium">Demo settings validated. Server saving is not connected yet.</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-sm text-rose-800 shadow-xs animate-fade-in">
          <i className="ri-error-warning-fill text-rose-600 text-lg"></i>
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Program identity card */}
        <div className="group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 p-5 md:p-6 shadow-sm transition-all duration-300 hover:shadow-lg flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-xs">
              <i className="ri-id-card-line text-lg"></i>
            </span>
            <div>
              <h4 className="font-heading font-bold text-base md:text-lg text-foreground-950">Program identity</h4>
              <p className="text-xs text-foreground-500 mt-0.5">How your loyalty program is branded and introduced.</p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="program-name" className="text-xs font-bold text-foreground-700 uppercase tracking-wider">
              Program display name
            </label>
            <input
              id="program-name"
              type="text"
              value={programName}
              onChange={(event) => setProgramName(event.target.value)}
              className={inputClasses}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="default-tier" className="text-xs font-bold text-foreground-700 uppercase tracking-wider">
              Default tier for new members
            </label>
            <div className="relative">
              <select
                id="default-tier"
                value={defaultTier}
                onChange={(event) => setDefaultTier(event.target.value)}
                className={`${inputClasses} appearance-none pr-9 cursor-pointer font-medium`}
              >
                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.name}>
                    {tier.name}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
        </div>

        {/* Earning rules card */}
        <div className="group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 p-5 md:p-6 shadow-sm transition-all duration-300 hover:shadow-lg flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xs">
              <i className="ri-coins-line text-lg"></i>
            </span>
            <div>
              <h4 className="font-heading font-bold text-base md:text-lg text-foreground-950">Earning rules</h4>
              <p className="text-xs text-foreground-500 mt-0.5">Conversion rate and member accrual logic.</p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="points-per-currency" className="text-xs font-bold text-foreground-700 uppercase tracking-wider">
              Points per unit spent ($1.00 USD)
            </label>
            <input
              id="points-per-currency"
              type="number"
              min={1}
              value={pointsPerCurrency}
              onChange={(event) => setPointsPerCurrency(event.target.value)}
              className={inputClasses}
            />
            <p className="text-xs text-foreground-400">Members earn this many points for each currency unit spent.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="signup-bonus" className="text-xs font-bold text-foreground-700 uppercase tracking-wider">
              Welcome signup bonus (points)
            </label>
            <input
              id="signup-bonus"
              type="number"
              min={0}
              value={signupBonus}
              onChange={(event) => setSignupBonus(event.target.value)}
              className={inputClasses}
            />
            <p className="text-xs text-foreground-400">Instant points granted immediately when a new member joins.</p>
          </div>
        </div>
      </div>

      {/* Redemption policy card */}
      <div className="group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 p-5 md:p-6 shadow-sm transition-all duration-300 hover:shadow-lg flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 text-white shadow-xs">
            <i className="ri-shield-check-line text-lg"></i>
          </span>
          <div>
            <h4 className="font-heading font-bold text-base md:text-lg text-foreground-950">Redemption approval policy</h4>
            <p className="text-xs text-foreground-500 mt-0.5">Control automated vs reviewed disbursement.</p>
          </div>
        </div>

        <div className="inline-flex items-center gap-1.5 p-1 rounded-xl bg-background-100 border border-background-200/80 self-start">
          <button
            type="button"
            onClick={() => setRedemptionPolicy('auto_approve')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 cursor-pointer ${
              redemptionPolicy === 'auto_approve'
                ? 'bg-emerald-500 text-white shadow-xs'
                : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            Auto-approve claims
          </button>
          <button
            type="button"
            onClick={() => setRedemptionPolicy('manual_review')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 cursor-pointer ${
              redemptionPolicy === 'manual_review'
                ? 'bg-primary-500 text-white shadow-xs'
                : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            Manual review queue
          </button>
        </div>

        <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl bg-background-100/50 p-3 rounded-xl border border-background-200/60">
          {redemptionPolicy === 'auto_approve'
            ? '✓ Redemptions are approved instantly and vouchers/rewards are delivered automatically. Points are immediately deducted from the member wallet.'
            : '⏱ Redemptions enter an approval queue for your ops team to review before points are deducted and vouchers are issued.'}
        </p>
      </div>

      {/* Tier thresholds table card */}
      <div className="group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 shadow-sm transition-all duration-300 hover:shadow-lg">
        <div className="px-5 md:px-6 py-4 border-b border-background-200/80 flex items-center gap-3">
          <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-xs">
            <i className="ri-vip-crown-line text-lg"></i>
          </span>
          <div>
            <h4 className="font-heading font-bold text-base md:text-lg text-foreground-950">Membership tier thresholds</h4>
            <p className="text-xs text-foreground-500 mt-0.5">Lifetime points required to reach each tier, in ascending order.</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-foreground-400 border-b border-background-200/80 bg-background-100/50">
                <th className="px-5 py-3.5 font-bold">Tier</th>
                <th className="px-5 py-3.5 font-bold w-48">Min lifetime points</th>
                <th className="px-5 py-3.5 font-bold">Tier perks & benefits</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {[...tiers]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((tier) => (
                  <tr key={tier.id} className="hover:bg-background-100/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 border border-primary-500/20 font-bold text-xs">
                          <i className="ri-vip-line text-base"></i>
                        </span>
                        <span className="font-bold text-foreground-950 whitespace-nowrap">{tier.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <input
                        type="number"
                        min={0}
                        value={tier.minPoints}
                        onChange={(event) => handleTierChange(tier.id, 'minPoints', event.target.value)}
                        className={inputClasses}
                      />
                    </td>
                    <td className="px-5 py-4">
                      <input
                        type="text"
                        value={tier.benefits}
                        onChange={(event) => handleTierChange(tier.id, 'benefits', event.target.value)}
                        className={inputClasses}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
